import { format, parse } from "date-fns";
import { useCallback } from "react";

import { Lab } from "pwapi";

import { toTitleCase } from "shared/utils/helpers";

import { AnyFilterKey, mapQueryFilterToOptions } from "./CaseAdminFilters";
import {
  CombinedFilterState,
  FilterOptions,
} from "./CaseAdminFilters.contracts";
import {
  isLabeledValueArray,
  matchCert,
  parseBoolean,
} from "./CaseAdminFilters.utils";
import { createMapCaseFilterToOptions } from "../filters/CaseFilters";
import { createMapSlidesFilterToOptions } from "../filters/SlidesFilters";
import { mapSpecimensFilterToOptions } from "../filters/SpecimenFilters";

const createMapFilterOptionToLabel = (labs: Lab[] = []) => {
  // Create full map from filter option to options array
  const mapFilterItemToOptions: FilterOptions<CombinedFilterState> = {
    ...createMapCaseFilterToOptions(labs),
    ...createMapSlidesFilterToOptions(labs),
    ...mapSpecimensFilterToOptions,
    ...mapQueryFilterToOptions,
  };

  // Return a function that that maps a Filter and a Value to its label
  return (filterItemKey: keyof CombinedFilterState, value: string): string => {
    const filterItemOptions = mapFilterItemToOptions[filterItemKey] || [];

    return filterItemOptions.find(({ value: v }) => v === value)?.label || "";
  };
};

export const useSanitizeFilterValue = (labs: any) => {
  return useCallback(
    (filterKey: AnyFilterKey, value: string) => {
      const mapFilterOptionToLabel = createMapFilterOptionToLabel(labs);

      // match possible system value to readable option label if applicable
      const sanitizedValue = mapFilterOptionToLabel(filterKey, value) || value;

      // TC CLIA - strip numeric id from compound value
      if (!!matchCert(sanitizedValue))
        return sanitizedValue.split(",").slice(0, -1).join(",");

      if (parseBoolean(sanitizedValue) !== undefined)
        return toTitleCase(sanitizedValue);

      // QA Review - JSON values to readable labels
      try {
        const jsonValue = JSON.parse(sanitizedValue);
        if (isLabeledValueArray(jsonValue))
          return jsonValue.map(({ label }) => label).join(", ");
      } catch (e) {}

      // System Dates - `yyyy-MM-dd` => `MM/dd/yyyy`
      try {
        return format(
          parse(sanitizedValue, "yyyy-MM-dd", new Date()),
          "MM/dd/yyyy"
        );
      } catch (e) {}

      return sanitizedValue;
    },
    [labs]
  );
};
